export const CATEGORY_NAME_MAX_LEN = 50;
export const PRODUCT_NAME_MAX_LEN = 50;
export const IMAGE_MAX_KB = 500;
export const IMAGE_MAX_BYTES = IMAGE_MAX_KB * 1024;
/** CTM/ITSM complaint attachments: video size cap. */
export const COMPLAINT_VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const COMPLAINT_ATTACHMENT_MAX_FILES = 5;
/** CTM complaint remarks (maps to issue_description). */
export const COMPLAINT_REMARKS_MAX_LEN = 100;
/** Behaviour tracking manager remarks. */
export const BEHAVIOUR_REMARKS_MAX_LEN = 80;
// Backward-compatible alias (some components still reference this name).
export const REMARKS_MAX_LENGTH = COMPLAINT_REMARKS_MAX_LEN;

const NAME_ALLOWED_RE = /^[A-Za-z0-9 ]+$/;
const NUMERIC_RE = /^[0-9]+$/;
const ALPHANUMERIC_RE = /^[A-Za-z0-9]+$/;
const EMP_ID_ALLOWED_RE = /^[A-Za-z0-9/]+$/;
// Intentionally strict (lowercase only) per UI requirement.
const SIMPLE_EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,10}$/;
// Address: allow common punctuation used in Indian addresses.
// Includes: letters, digits, space, comma, dot, hyphen, slash, #, (), apostrophe.
const ADDRESS_ALLOWED_RE = /^[A-Za-z0-9 ,.\-/#()']*$/;
const FSSAI_RE = /^[0-9]{14}$/;
// GSTIN format: 15 chars: 2 digits + 10 PAN + 1 entity + Z + checksum
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export function validateSimpleName(
  value,
  { fieldLabel = 'Name', maxLen = CATEGORY_NAME_MAX_LEN } = {}
) {
  const trimmed = String(value ?? '').trim();

  if (!trimmed) return `Please enter ${fieldLabel.toLowerCase()}`;
  if (trimmed.length > maxLen) return `${fieldLabel} must be ${maxLen} characters or less`;
  if (!NAME_ALLOWED_RE.test(trimmed)) return `${fieldLabel} should not contain special characters`;

  return '';
}

export function validateCategoryName(value) {
  return validateSimpleName(value, { fieldLabel: 'Category name' });
}

export function validateSubCategoryName(value) {
  return validateSimpleName(value, { fieldLabel: 'Sub category name' });
}

/** Letters only; at most one space between words; no digits or special characters. */
const EMPLOYEE_NAME_RE = /^[A-Za-z]+( [A-Za-z]+)*$/;

export function validateEmployeeName(
  value,
  { fieldLabel = 'Employee name', maxLen = 50 } = {}
) {
  const trimmed = String(value ?? '').trim();

  if (!trimmed) return `Please enter ${fieldLabel.toLowerCase()}`;
  if (trimmed.length > maxLen) return `${fieldLabel} must be ${maxLen} characters or less`;
  if (!EMPLOYEE_NAME_RE.test(trimmed))
    return `${fieldLabel} must use only letters, with a single space between words (no digits or special characters)`;

  return '';
}

export function validateEmployeeId(value, { fieldLabel = 'Employee id', maxLen = 50 } = {}) {
  const trimmed = String(value ?? '').trim();
  const required = validateRequired(trimmed, fieldLabel);
  if (required) return required;
  if (trimmed.length > maxLen) return `${fieldLabel} must be ${maxLen} characters or less`;
  if (!EMP_ID_ALLOWED_RE.test(trimmed))
    return `${fieldLabel} can only contain letters, numbers, and /`;
  return '';
}

export function validateRequired(value, fieldLabel = 'This field') {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return `Please select ${fieldLabel.toLowerCase()}`;
  return '';
}

/** Local calendar date as YYYY-MM-DD (for `<input type="date">` max/min). */
export function todayDateInputValue(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Date input value must be today or earlier (lexicographic YYYY-MM-DD compare). */
export function validateDateNotAfterToday(value, fieldLabel = 'Date') {
  const required = validateRequired(value, fieldLabel);
  if (required) return required;
  if (String(value).trim() > todayDateInputValue()) {
    return `${fieldLabel} cannot be after today`;
  }
  return '';
}

export function validateMaxLen(value, maxLen, fieldLabel = 'This field') {
  const trimmed = String(value ?? '').trim();
  if (trimmed.length > maxLen) return `${fieldLabel} must be ${maxLen} characters or less`;
  return '';
}

export function validateNumeric(value, fieldLabel = 'This field') {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return `Please enter ${fieldLabel.toLowerCase()}`;
  if (!NUMERIC_RE.test(trimmed)) return `${fieldLabel} must be a number`;
  return '';
}

export function validateAlphaNumeric(value, fieldLabel = 'This field') {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return `Please enter ${fieldLabel.toLowerCase()}`;
  if (!ALPHANUMERIC_RE.test(trimmed))
    return `${fieldLabel} should contain only letters and numbers`;
  return '';
}

export function validateEmail(value, fieldLabel = 'Email id') {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return `Please enter ${fieldLabel.toLowerCase()}`;
  if (/[A-Z]/.test(trimmed)) return `${fieldLabel} should be in lowercase`;
  if (!SIMPLE_EMAIL_RE.test(trimmed)) return `Please enter a valid ${fieldLabel.toLowerCase()}`;
  return '';
}

export function validateContactDetail(value, { digits = 10, startsWith = [6, 7, 8, 9] } = {}) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return 'Please enter contact detail';
  if (!NUMERIC_RE.test(trimmed)) return 'Contact detail must contain only numbers';
  if (digits && trimmed.length !== digits) return `Contact detail must be ${digits} digits`;
  if (startsWith?.length) {
    const first = Number(trimmed[0]);
    if (!startsWith.includes(first)) return 'Contact detail must start from 6';
  }
  return '';
}

export function validateAddress(value, { maxLen = 100 } = {}) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return 'Please enter address';
  if (trimmed.length > maxLen) return `Address must be ${maxLen} characters or less`;
  if (!ADDRESS_ALLOWED_RE.test(trimmed))
    return "Address should contain only letters, numbers, spaces, and these: , . - / # ( ) '";
  return '';
}

export function validateFssaiNumber(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  // Allow users to type spaces/dashes, but FSSAI license is 14 digits.
  // Reject alphabetic characters explicitly to avoid false positives after normalization.
  if (/[A-Za-z]/.test(trimmed)) return 'FSSAI number must contain only digits';
  const normalized = trimmed.replace(/[\s-]/g, '');
  if (!FSSAI_RE.test(normalized)) return 'FSSAI number must be 14 digits';
  return '';
}

export function validateGstNumber(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  if (!GST_RE.test(trimmed)) return 'Please enter a valid GST number';
  return '';
}

export function validateProductName(value) {
  const required = validateRequired(value, 'Item name');
  if (required) return required;
  return validateMaxLen(value, PRODUCT_NAME_MAX_LEN, 'Item name');
}

export function validateImageFilesUnderSize(files, { maxBytes = IMAGE_MAX_BYTES } = {}) {
  const list = Array.isArray(files) ? files : Array.from(files || []);
  if (!list.length) return '';
  const tooLarge = list.find((f) => Number(f?.size || 0) > maxBytes);
  if (tooLarge) return `Image size should be ${Math.round(maxBytes / 1024)}KB or less`;
  return '';
}

/** Single file from complaint image input; empty if no file. */
export function validateComplaintImageFileSize(file, { maxBytes = IMAGE_MAX_BYTES } = {}) {
  if (!(file instanceof File)) return '';
  return validateImageFilesUnderSize([file], { maxBytes });
}

/**
 * New complaint attachment files only (not existing server URLs).
 * Images: IMAGE_MAX_BYTES. Videos: COMPLAINT_VIDEO_MAX_BYTES. Max 5 files in one selection batch check left to caller.
 */
export function validateComplaintMediaFiles(
  files,
  { maxImageBytes = IMAGE_MAX_BYTES, maxVideoBytes = COMPLAINT_VIDEO_MAX_BYTES } = {}
) {
  const list = Array.isArray(files) ? files : Array.from(files || []);
  for (const f of list) {
    if (!(f instanceof File)) continue;
    const type = String(f.type || '');
    const isVideo = type.startsWith('video/');
    const isImage = type.startsWith('image/');
    if (!isVideo && !isImage) {
      return `File "${f.name}" must be an image or a video`;
    }
    const maxB = isVideo ? maxVideoBytes : maxImageBytes;
    if (Number(f.size || 0) > maxB) {
      return isVideo
        ? `Video "${f.name}" must be ${Math.round(maxVideoBytes / (1024 * 1024))}MB or less`
        : `Image "${f.name}" must be ${Math.round(maxImageBytes / 1024)}KB or less`;
    }
  }
  return '';
}

function complaintFieldRequired(value, label) {
  if (String(value ?? '').trim() !== '') return '';
  return `${label} is required.`;
}

/**
 * Optional free text: only max length when non-empty.
 */
export function validateComplaintRemarks(value) {
  const trimmed = String(value ?? '');
  if (trimmed.length > COMPLAINT_REMARKS_MAX_LEN)
    return `Remarks must be ${COMPLAINT_REMARKS_MAX_LEN} characters or less`;
  return '';
}

/**
 * Optional field: empty OK; otherwise digits only.
 */
export function validateOptionalDigitsOnly(value, fieldLabel) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (!NUMERIC_RE.test(s)) return `${fieldLabel} must contain only digits`;
  return '';
}

export const COMPLAINT_QUANTITY_NA = 'N/A';

function parseComplaintQuantityNumber(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function isComplaintQuantityNa(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  if (s.replace(/\s/g, '').toUpperCase() === 'N/A') return true;
  const n = parseComplaintQuantityNumber(s);
  return n === 0;
}

export function complaintQuantityToFormValue(value) {
  if (value == null || String(value).trim() === '') return '';
  if (isComplaintQuantityNa(value)) return COMPLAINT_QUANTITY_NA;
  const n = parseComplaintQuantityNumber(value);
  if (n != null && n > 0) return String(Math.trunc(n));
  return '';
}

export function complaintQuantityToPayload(value) {
  if (isComplaintQuantityNa(value)) return 0;
  const n = parseComplaintQuantityNumber(value);
  if (n == null || n <= 0) return undefined;
  return Math.trunc(n);
}

export function formatComplaintQuantity(value) {
  if (value == null || String(value).trim() === '') return '—';
  if (isComplaintQuantityNa(value)) return COMPLAINT_QUANTITY_NA;
  const n = parseComplaintQuantityNumber(value);
  if (n != null) return String(Math.trunc(n));
  return String(value);
}

export function complaintUomToFormValue(value) {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim();
  if (s === '0' || s.replace(/\s/g, '').toUpperCase() === 'N/A') return COMPLAINT_QUANTITY_NA;
  return s;
}

export function formatComplaintUom(value) {
  if (value == null || String(value).trim() === '') return '—';
  return complaintUomToFormValue(value) || '—';
}

/**
 * Optional field: empty OK; otherwise letters + numbers only.
 */
export function validateOptionalAlphaNumeric(value, fieldLabel) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (!ALPHANUMERIC_RE.test(s))
    return `${fieldLabel} should contain only letters and numbers`;
  return '';
}

/**
 * CTM add/edit complaint: mandatory fields + image + optional remarks / quantity / batch rules.
 * @returns {Record<string, string>} Field key → error message (empty object if valid).
 */
export function validateCtmComplaintForm({
  location,
  managerOnDuty,
  raisedBy,
  incidentDate,
  reportedDate,
  priority,
  vehicleStorageTemperature,
  issueType,
  department,
  category,
  subCategory,
  productId,
  productCategoryId,
  productSubcategoryId,
  remarks,
  quantity,
  uom,
  batchNo,
  mfgDate,
  bestBefore,
  keptExistingAttachments,
  newAttachmentFiles,
} = {}) {
  const errs = {};
  const add = (key, msg) => {
    if (msg) errs[key] = msg;
  };

  add('location', complaintFieldRequired(location, 'Location'));
  add('raisedBy', complaintFieldRequired(raisedBy, 'Complaint Raised by'));
  add('incidentDate', complaintFieldRequired(incidentDate, 'Date of Incident'));
  add('reportedDate', complaintFieldRequired(reportedDate, 'Reported Date'));
  add('priority', complaintFieldRequired(priority, 'Work Priority'));
  add('issueType', complaintFieldRequired(issueType, 'Issue Type'));
  add('department', complaintFieldRequired(department, 'Accountable Department'));
  add('category', complaintFieldRequired(category, 'Category'));
  add('subCategory', complaintFieldRequired(subCategory, 'Sub Category'));
  add('productId', complaintFieldRequired(productId, 'Product'));
  add('productCategoryId', complaintFieldRequired(productCategoryId, 'Product Category'));
  add('productSubcategoryId', complaintFieldRequired(productSubcategoryId, 'Product Sub Category'));

  add(
    'vehicleStorageTemperature',
    complaintFieldRequired(vehicleStorageTemperature, 'Vehicle Storage Temperature')
  );

  add('remarks', complaintFieldRequired(remarks, 'Remarks') || validateComplaintRemarks(remarks));

  add('quantity', complaintFieldRequired(quantity, 'Quantity'));
  if (!isComplaintQuantityNa(quantity)) {
    add('quantity', validateOptionalDigitsOnly(quantity, 'Quantity'));
  }

  add('uom', complaintFieldRequired(uom, 'UOM'));

  add('batchNo', validateAlphaNumeric(batchNo, 'Batch no.'));
  add('mfgDate', complaintFieldRequired(mfgDate, 'MFG date'));
  add('bestBefore', complaintFieldRequired(bestBefore, 'Use before/Best before'));

  const keptN = Array.isArray(keptExistingAttachments) ? keptExistingAttachments.length : 0;
  const newN = Array.isArray(newAttachmentFiles) ? newAttachmentFiles.length : 0;
  const total = keptN + newN;
  if (total < 1) {
    add('attachments', 'At least one image or video is required.');
  } else if (total > COMPLAINT_ATTACHMENT_MAX_FILES) {
    add('attachments', `You can upload at most ${COMPLAINT_ATTACHMENT_MAX_FILES} files.`);
  } else {
    add('attachments', validateComplaintMediaFiles(newAttachmentFiles));
  }

  return errs;
}

/** ITSM: optional issue description; max length only (not required). */
export function validateItsmIssueDescription(value) {
  const len = String(value ?? '').length;
  if (len > COMPLAINT_REMARKS_MAX_LEN)
    return `Issue description must be ${COMPLAINT_REMARKS_MAX_LEN} characters or less`;
  return '';
}

/**
 * ITSM add/edit complaint: mandatory fields + image + optional issue description length.
 * @returns {Record<string, string>} Field key → error message (empty object if valid).
 */
export function validateItsmComplaintForm({
  location,
  managerOnDuty,
  raisedBy,
  incidentDate,
  category,
  subCategory,
  issueType,
  department,
  priority,
  equipment,
  building,
  issueDescription,
  keptExistingAttachments,
  newAttachmentFiles,
} = {}) {
  const errs = {};
  const add = (key, msg) => {
    if (msg) errs[key] = msg;
  };

  add('location', complaintFieldRequired(location, 'Location'));
  add('raisedBy', complaintFieldRequired(raisedBy, 'Complaint Raised by'));
  add('incidentDate', complaintFieldRequired(incidentDate, 'Date of Incident'));
  add('category', complaintFieldRequired(category, 'Category'));
  add('subCategory', complaintFieldRequired(subCategory, 'Sub Category'));
  add('issueType', complaintFieldRequired(issueType, 'Issue Type'));
  add('department', complaintFieldRequired(department, 'Accountable Department'));
  add('priority', complaintFieldRequired(priority, 'Work Priority'));
  // R&M is not part of the ITSM form flow currently; keep any legacy fields optional.
  void equipment;
  void building;

  add(
    'issueDescription',
    complaintFieldRequired(issueDescription, 'Issue Description') ||
      validateItsmIssueDescription(issueDescription)
  );

  const keptN = Array.isArray(keptExistingAttachments) ? keptExistingAttachments.length : 0;
  const newN = Array.isArray(newAttachmentFiles) ? newAttachmentFiles.length : 0;
  const total = keptN + newN;
  if (total < 1) {
    add('attachments', 'At least one image or video is required.');
  } else if (total > COMPLAINT_ATTACHMENT_MAX_FILES) {
    add('attachments', `You can upload at most ${COMPLAINT_ATTACHMENT_MAX_FILES} files.`);
  } else {
    add('attachments', validateComplaintMediaFiles(newAttachmentFiles));
  }

  return errs;
}

export function validateAadharNumber(value, fieldLabel = 'Aadhar number') {
  const trimmed = String(value ?? '').trim();
  const required = validateRequired(trimmed, fieldLabel);
  if (required) return required;
  if (!NUMERIC_RE.test(trimmed)) return `${fieldLabel} must contain only numbers`;
  if (trimmed.length !== 12) return `${fieldLabel} must be 12 digits`;
  return '';
}

/** Indian PAN: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F). */
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function validatePanNumber(value, fieldLabel = 'PAN number') {
  const trimmed = String(value ?? '').trim().toUpperCase();
  const required = validateRequired(trimmed, fieldLabel);
  if (required) return required;
  if (trimmed.length !== 10) return `${fieldLabel} must be 10 characters`;
  if (!PAN_RE.test(trimmed))
    return `${fieldLabel} must be in format AAAAA9999A (5 letters, 4 digits, 1 letter)`;
  return '';
}

const PRODUCT_UNIT_RE = /^[A-Za-z]+$/;
const PRODUCT_UNIT_MAX_LEN = 20;

/** Optional percent 0–100 (Buffer, Item Category). */
export function clampPercentInput(value, { max = 100 } = {}) {
  const raw = String(value ?? '');
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (Number.isNaN(n)) return '';
  if (n < 0) return '0';
  if (n > max) return String(max);
  return raw;
}

export function validateOptionalPercent(value, fieldLabel, { max = 100 } = {}) {
  const s = String(value ?? '')
    .trim()
    .replace(/%/g, '');
  if (!s) return '';
  const n = Number(s);
  if (Number.isNaN(n)) return `${fieldLabel} must be a number`;
  if (n < 0 || n > max) return `${fieldLabel} must be between 0 and ${max}`;
  return '';
}

export function sanitizeUnitInput(value) {
  return String(value ?? '')
    .replace(/[^A-Za-z]/g, '')
    .slice(0, PRODUCT_UNIT_MAX_LEN);
}

export function validateOptionalUnit(value, fieldLabel = 'Unit') {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (!PRODUCT_UNIT_RE.test(s))
    return `${fieldLabel} should contain only letters (e.g. Kg, Nos)`;
  if (s.length > PRODUCT_UNIT_MAX_LEN)
    return `${fieldLabel} must be ${PRODUCT_UNIT_MAX_LEN} characters or less`;
  return '';
}

export const PRODUCT_SPS_MAX = 10;

export function sanitizeNonNegativeDecimal(value, { max = PRODUCT_SPS_MAX } = {}) {
  const raw = String(value ?? '');
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (Number.isNaN(n)) return '';
  if (n < 0) return '0';
  if (n > max) return String(max);
  return raw;
}

export function validateOptionalNonNegativeNumber(
  value,
  fieldLabel,
  { max = PRODUCT_SPS_MAX } = {}
) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const n = Number(s);
  if (Number.isNaN(n)) return `${fieldLabel} must be a number`;
  if (n < 0 || n > max) return `${fieldLabel} must be between 0 and ${max}`;
  return '';
}
